import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { parseCoordinatesFromString } from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getStringFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoShipDeckCoamingJSON {
  type: GeckoFeatureModelType.ShipDeckCoaming
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
    lineThickness: number
  }
}

export const NewGeckoShipDeckCoamingJSON: GeckoShipDeckCoamingJSON = {
  type: GeckoFeatureModelType.ShipDeckCoaming,
  name: 'New Ship Deck Coaming',
  feature_geometry: {
    coordinates: [{ x: 0, y: 0 }],
    lineThickness: 0.5,
  },
}

export class GeckoShipDeckCoaming extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckCoaming
  readonly coordinates: {
    readonly x: GeckoMeasurement
    readonly y: GeckoMeasurement
  }[]
  readonly lineThickness = 0.5

  /**
   * @param id
   * @param name
   * @param coordinates
   */
  private constructor(
    id: string,
    name: string,
    coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[]
  ) {
    super(id)
    this.name = name
    this.coordinates = coordinates
  }

  public static CreateFromJSON(
    componentID: string,
    coamingID: string,
    coamingJSON: GeckoShipDeckCoamingJSON
  ): GeckoShipDeckCoaming {
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const coamingCoordinates = coamingJSON.feature_geometry.coordinates.map(
      (coord) => ({
        x: GeckoMeasurement.CreateFromValue(coord.x, polygonUnits.x.unit),
        y: GeckoMeasurement.CreateFromValue(coord.y, polygonUnits.y.unit),
      })
    )

    const coamingName = coamingJSON.name as string

    return new GeckoShipDeckCoaming(coamingID, coamingName, coamingCoordinates)
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckCoamingJSON>
  ): Map<string, GeckoShipDeckCoaming> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckCoaming.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckCoaming>())
  }

  asJSON() {
    return generateCoaming(this.name, this.coordinates)
  }
}

export function generateCoaming(
  name: string,
  coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[]
): GeckoShipDeckCoamingJSON {
  return {
    name: name,
    type: GeckoFeatureModelType.ShipDeckCoaming,
    feature_geometry: {
      coordinates: coordinates.map((coord) => ({
        x: coord.x.toNumber(GeckoMeasurementUnit.Meters),
        y: coord.y.toNumber(GeckoMeasurementUnit.Meters),
      })),
      lineThickness: 2,
    },
  }
}

export const ShipDeckCoamingFormFields = (
  coamingJSON: GeckoShipDeckCoamingJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Coaming Name',
      value: coamingJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: coamingJSON.feature_geometry.coordinates
        .map((coord) => `[${coord.x}, ${coord.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(2),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
  ]
}

export const ShipDeckCoamingJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getStringFieldByDataKey(formFields, 'coordinates')

  return generateCoaming(
    getStringFieldByDataKey(formFields, 'name')!,
    parseCoordinatesFromString(coordinates).map(({ x, y }) => ({
      x: GeckoMeasurement.CreateFromValue(x, GeckoMeasurementUnit.Meters),
      y: GeckoMeasurement.CreateFromValue(y, GeckoMeasurementUnit.Meters),
    }))
  )
}

export const ShipDeckCoamingFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckCoaming,
  title: 'Coamings',
  singularName: 'Coaming',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to enter coordinates for coaming, please refer to this page. TODO!',
}
